import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getMe, updateNotificationPrefs } from '../api/client';
import type { NotificationPrefs, SubscriberMe } from '../api/types';
import { useAuth } from '../auth/useAuth';
import { Card } from '../components/Card';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';

type FormState = {
  phoneE164: string;
  notifyEmail: boolean;
  notifySms: boolean;
  notifyDailySummary: boolean;
  notifyUrgentAlerts: boolean;
};

function toForm(me: SubscriberMe): FormState {
  return {
    phoneE164: me.phoneE164 ?? '',
    notifyEmail: me.notifyEmail,
    notifySms: me.notifySms,
    notifyDailySummary: me.notifyDailySummary,
    notifyUrgentAlerts: me.notifyUrgentAlerts,
  };
}

export function AccountPage() {
  const { getIdToken, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => getMe(getIdToken) });

  const [form, setForm] = useState<FormState | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the form once the subscriber loads.
  useEffect(() => {
    if (meQuery.data && form === null) setForm(toForm(meQuery.data));
  }, [meQuery.data, form]);

  const saveMutation = useMutation({
    onMutate: () => {
      setNote(null);
      setError(null);
    },
    mutationFn: (prefs: Partial<NotificationPrefs>) => updateNotificationPrefs(prefs, getIdToken),
    onSuccess: (res) => {
      queryClient.setQueryData<SubscriberMe>(['me'], (old) => (old ? { ...old, ...res } : old));
      setForm(toForm({ ...(meQuery.data as SubscriberMe), ...res }));
      setNote('Saved.');
    },
    onError: (err: Error) => {
      // Surface the API's message (e.g. "Add a phone number to receive text alerts.").
      const m = /failed: \d+ (.+)$/.exec(err.message);
      let msg = 'Could not save. Please try again.';
      try {
        if (m) msg = (JSON.parse(m[1]) as { error?: string }).error ?? msg;
      } catch {
        /* keep default */
      }
      setError(msg);
    },
  });

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  return (
    <>
      <PageHeader title="Account" />
      <Card className="p-5 max-w-[460px] flex flex-col gap-4 mb-6">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-hs-overline text-hs-text-mute">Name</div>
          <div className="text-[15px] text-hs-text mt-0.5">{meQuery.data?.displayName ?? '—'}</div>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-hs-overline text-hs-text-mute">Email</div>
          <div className="text-[15px] text-hs-text mt-0.5">{meQuery.data?.email ?? '—'}</div>
        </div>
        <div className="pt-1">
          <Button variant="ghost" onClick={handleSignOut}>Sign out</Button>
        </div>
      </Card>

      <Card className="p-5 max-w-[460px] flex flex-col gap-4">
        <div>
          <h2 className="font-display font-bold text-[17px] text-hs-text">Notifications</h2>
          <p className="text-[13px] text-hs-text-mute mt-0.5">
            Get a summary after each check-in, and an immediate alert if something needs attention.
          </p>
        </div>

        {form && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveMutation.mutate({ ...form, phoneE164: form.phoneE164.trim() || null });
            }}
          >
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute">
                Mobile number (for texts)
              </span>
              <input
                type="tel"
                inputMode="tel"
                placeholder="+15551234567"
                className="w-full rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text"
                value={form.phoneE164}
                onChange={(e) => set('phoneE164', e.target.value)}
              />
            </label>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute mb-1">
                How to reach you
              </legend>
              <Toggle
                label="Email"
                hint="A short alert with a link to the dashboard"
                checked={form.notifyEmail}
                onChange={(v) => set('notifyEmail', v)}
              />
              <Toggle
                label="Text message"
                hint="A short alert with a link to the dashboard"
                checked={form.notifySms}
                onChange={(v) => set('notifySms', v)}
              />
              <p className="text-[12px] leading-relaxed text-hs-text-mute">
                By turning on text messages, you agree to receive automated
                check-in alerts from Hey Be Sure at the number above (about one
                per check-in, plus urgent alerts). Msg &amp; data rates may apply.
                Reply STOP to opt out or HELP for help. See our{' '}
                <a
                  href="https://heybesure.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-hs-text"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://heybesure.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-hs-text"
                >
                  Terms
                </a>
                .
              </p>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute mb-1">
                When to notify you
              </legend>
              <Toggle
                label="After each check-in"
                hint="A summary once the call is done"
                checked={form.notifyDailySummary}
                onChange={(v) => set('notifyDailySummary', v)}
              />
              <Toggle
                label="Right away if there's a problem"
                hint="Immediate alert when a check-in flags a concern"
                checked={form.notifyUrgentAlerts}
                onChange={(v) => set('notifyUrgentAlerts', v)}
              />
            </fieldset>

            <div className="flex items-center gap-3">
              <Button type="submit" loading={saveMutation.isPending}>Save</Button>
              {note && <span className="text-[13px] text-hs-success-text">{note}</span>}
              {error && (
                <span className="text-[13px] text-hs-danger-text" role="alert">
                  {error}
                </span>
              )}
            </div>
          </form>
        )}
      </Card>
    </>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-hs-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex flex-col">
        <span className="text-[14px] text-hs-text">{label}</span>
        <span className="text-[12px] text-hs-text-mute">{hint}</span>
      </span>
    </label>
  );
}
